import FootballService from './footballServices';
import { getLeagueById, getCurrentSeasonInfo } from '../utils/footballLeagueUtils';

/**
 * Extended Football Service
 * Adds league configuration awareness to the base FootballService
 */
class FootballServiceExtended {
  constructor() {
    this.baseService = FootballService;
  }

  /**
   * Get fixtures with automatic season detection
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response with fixtures
   */
  async getFixturesWithSeasonDetection(params = {}) {
    const { league, date, ...otherParams } = params;
    
    // If league is specified, try to get current season info
    if (league) {
      const seasonInfo = getCurrentSeasonInfo(league);
      console.log(`🗓️ Season info for league ${league}:`, seasonInfo);
      
      if (seasonInfo && seasonInfo.current) {
        console.log(`✅ Using season ${seasonInfo.season} for league ${league}`);
        return this.baseService.getFixtures(
          null, null, null, date, league, 
          seasonInfo.season, null, null, null, 
          null, null, null, null, null, 'UTC'
        );
      } else {
        console.log(`⚠️ No season info found for league ${league}, using default`);
      }
    }
    
    // Fall back to regular fixtures call
    return this.baseService.getFixtures(
      null, null, null, date, league, 
      null, null, null, null, null, 
      null, null, null, null, 'UTC'
    );
  }

  /**
   * Get standings with automatic season detection
   * @param {number} leagueId - League ID
   * @param {string} [season] - Optional season override
   * @returns {Promise<Object>} API response with standings
   */
  async getStandingsWithSeasonDetection(leagueId, season = null) {
    // If no season provided, try to get current season
    if (!season) {
      const seasonInfo = getCurrentSeasonInfo(leagueId);
      if (seasonInfo && seasonInfo.current) {
        season = seasonInfo.season;
      } else {
        // Default to current year
        season = new Date().getFullYear().toString();
      }
    }
    
    return this.baseService.getStandings(leagueId, season);
  }

  /**
   * Get top scorers with automatic season detection
   * @param {number} leagueId - League ID
   * @param {string} [season] - Optional season override
   * @returns {Promise<Object>} API response with top scorers
   */
  async getTopScorersWithSeasonDetection(leagueId, season = null) {
    // If no season provided, try to get current season
    if (!season) {
      const seasonInfo = getCurrentSeasonInfo(leagueId);
      if (seasonInfo && seasonInfo.current) {
        season = seasonInfo.season;
      } else {
        // Default to current year
        season = new Date().getFullYear().toString();
      }
    }
    
    return this.baseService.getTopScorers(leagueId, season);
  }

  /**
   * Get fixtures by country
   * @param {string} countryName - Country name
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of fixtures
   */
  async getFixturesByCountry(countryName, date) {
    // For "World" competitions, we need to handle differently
    if (countryName === 'World') {
      // Get fixtures for major international competitions
      const worldLeagueIds = [2, 1, 4, 9, 13, 15, 16, 17]; // UCL, World Cup, Euro, Copa America, etc.
      const fixtures = [];
      
      for (const leagueId of worldLeagueIds) {
        try {
          const response = await this.getFixturesWithSeasonDetection({ league: leagueId, date });
          if (response.response && response.response.length > 0) {
            fixtures.push(...response.response);
          }
        } catch (error) {
          console.warn(`Failed to fetch fixtures for league ${leagueId}:`, error);
        }
      }
      
      return fixtures;
    }
    
    // For regular countries, fetch all fixtures and filter
    const response = await this.baseService.getFixtures(
      null, null, null, date, null, null, null, null, null, null, null, null, null, null, 'UTC'
    );
    
    const fixtures = response.response || [];
    
    // Filter by country
    return fixtures.filter(fixture => 
      fixture.league?.country === countryName ||
      fixture.league?.country?.name === countryName
    );
  }

  /**
   * Get live fixtures with league filtering
   * @param {Array<number>} leagueIds - Array of league IDs to filter
   * @returns {Promise<Object>} API response with live fixtures
   */
  async getLiveFixturesFiltered(leagueIds = null) {
    if (leagueIds && leagueIds.length > 0) {
      // API supports comma-separated league IDs for live fixtures
      return this.baseService.getLiveFixtures(leagueIds.join('-'));
    }
    
    return this.baseService.getLiveFixtures();
  }

  /**
   * Get fixtures for multiple leagues on a specific date
   * @param {Array<number>} leagueIds - Array of league IDs
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of fixtures
   */
  async getFixturesForMultipleLeagues(leagueIds, date) {
    const allFixtures = [];
    
    // Batch requests to avoid rate limiting
    const batchSize = 5;
    for (let i = 0; i < leagueIds.length; i += batchSize) {
      const batch = leagueIds.slice(i, i + batchSize);
      
      const batchPromises = batch.map(leagueId => 
        this.getFixturesWithSeasonDetection({ league: leagueId, date })
          .catch(error => {
            console.warn(`Failed to fetch fixtures for league ${leagueId}:`, error);
            return { response: [] };
          })
      );
      
      const batchResults = await Promise.all(batchPromises);
      
      batchResults.forEach(result => {
        if (result.response && result.response.length > 0) {
          allFixtures.push(...result.response);
        }
      });
      
      // Small delay between batches to avoid rate limiting
      if (i + batchSize < leagueIds.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    // Sort by fixture time
    return allFixtures.sort((a, b) => 
      new Date(a.fixture.date) - new Date(b.fixture.date)
    );
  }

  /**
   * Get league details with enriched information
   * @param {number} leagueId - League ID
   * @returns {Object} Enriched league information
   */
  async getEnrichedLeagueInfo(leagueId) {
    const leagueConfig = getLeagueById(leagueId);
    const seasonInfo = getCurrentSeasonInfo(leagueId);
    
    try {
      const apiLeagueInfo = await this.baseService.getLeagues(leagueId);
      
      return {
        ...leagueConfig,
        ...apiLeagueInfo.response?.[0]?.league,
        currentSeason: seasonInfo,
        hasCurrentSeason: !!seasonInfo
      };
    } catch (error) {
      console.warn(`Failed to fetch API info for league ${leagueId}:`, error);
      return {
        ...leagueConfig,
        currentSeason: seasonInfo,
        hasCurrentSeason: !!seasonInfo
      };
    }
  }

  /**
   * Test connection with better error handling
   * @returns {Promise<Object>} Connection test result
   */
  async testConnection() {
    const result = await this.baseService.testConnection();
    
    if (result.success) {
      // Try to fetch a popular league to ensure full API access
      try {
        const plFixtures = await this.getFixturesWithSeasonDetection({ 
          league: 39, // Premier League
          date: this.baseService.getTodayDate() 
        });
        
        return {
          ...result,
          fullAccess: true,
          sampleFixtureCount: plFixtures.response?.length || 0
        };
      } catch (error) {
        return {
          ...result,
          fullAccess: false,
          accessError: error.message
        };
      }
    }
    
    return result;
  }

  /**
   * Get today's date in YYYY-MM-DD format
   * @returns {string} Today's date
   */
  getTodayDate() {
    return this.baseService.getTodayDate();
  }

  // Delegate all other methods to the base service
  __proto__ = FootballService;
}

// Create a singleton instance
const extendedService = new FootballServiceExtended();

// Proxy all FootballService methods that aren't overridden
Object.getOwnPropertyNames(Object.getPrototypeOf(FootballService)).forEach(method => {
  if (method !== 'constructor' && !extendedService[method] && typeof FootballService[method] === 'function') {
    extendedService[method] = (...args) => FootballService[method](...args);
  }
});

// Also proxy non-function properties
Object.keys(FootballService).forEach(key => {
  if (!extendedService[key]) {
    extendedService[key] = FootballService[key];
  }
});

// Add a debug method to test Brazil leagues
extendedService.testBrazilLeagues = async () => {
  console.log('🧪 Testing Brazil leagues API calls...');
  
  // Test different seasons for Serie A and B
  const seasonTests = [
    { league: 71, season: 2025, desc: 'Serie A with 2025' },
    { league: 71, season: 2024, desc: 'Serie A with 2024' },
    { league: 71, season: null, desc: 'Serie A no season' },
    { league: 72, season: 2025, desc: 'Serie B with 2025' },
    { league: 72, season: 2024, desc: 'Serie B with 2024' },
    { league: 72, season: null, desc: 'Serie B no season' },
  ];
  
  const today = FootballService.getTodayDate();
  
  console.log('📅 Testing with today\'s date:', today);
  for (const test of seasonTests) {
    try {
      const response = await FootballService.getFixtures(
        null, null, null, today, test.league,
        test.season, null, null, null, null, null, null, null, null, 'UTC'
      );
      console.log(`✅ ${test.desc}: ${response.response?.length || 0} fixtures`);
      if (response.response?.length > 0) {
        console.log('   First fixture date:', response.response[0].fixture.date);
      }
    } catch (error) {
      console.log(`❌ ${test.desc}: Error - ${error.message}`);
    }
  }
  
  // Test Serie A across multiple dates
  console.log('\n🗓️ Testing Serie A across different dates with season 2024:');
  const dates = [
    '2025-08-04', '2025-08-05', '2025-08-06', '2025-08-07', 
    '2025-08-08', '2025-08-09', '2025-08-10'
  ];
  
  for (const date of dates) {
    try {
      const response = await FootballService.getFixtures(
        null, null, null, date, 71,
        2024, null, null, null, null, null, null, null, null, 'UTC'
      );
      if (response.response?.length > 0) {
        console.log(`✅ ${date}: ${response.response.length} Serie A fixtures found!`);
      }
    } catch (error) {
      console.log(`❌ ${date}: Error`);
    }
  }
  
  console.log('🧪 Test complete!');
};

// Quick test for all Brazil Serie leagues
extendedService.testAllBrazilSeries = async () => {
  console.log('🇧🇷 Testing all Brazil Serie leagues (A, B, C, D)...');
  
  const series = [
    { id: 71, name: 'Serie A' },
    { id: 72, name: 'Serie B' },
    { id: 75, name: 'Serie C' },
    { id: 76, name: 'Serie D' }
  ];
  
  const today = FootballService.getTodayDate();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  
  for (const serie of series) {
    console.log(`\n📊 Testing ${serie.name} (ID: ${serie.id}):`);
    
    // Test today with season 2025
    try {
      const todayResponse = await FootballService.getFixtures(
        null, null, null, today, serie.id,
        2025, null, null, null, null, null, null, null, null, 'UTC'
      );
      console.log(`  Today (${today}): ${todayResponse.response?.length || 0} fixtures`);
      
      const tomorrowResponse = await FootballService.getFixtures(
        null, null, null, tomorrowStr, serie.id,
        2025, null, null, null, null, null, null, null, null, 'UTC'
      );
      console.log(`  Tomorrow (${tomorrowStr}): ${tomorrowResponse.response?.length || 0} fixtures`);
      
      // Test without season as fallback
      if (todayResponse.response?.length === 0) {
        const noSeasonResponse = await FootballService.getFixtures(
          null, null, null, today, serie.id,
          null, null, null, null, null, null, null, null, 'UTC'
        );
        console.log(`  Today without season: ${noSeasonResponse.response?.length || 0} fixtures`);
      }
    } catch (error) {
      console.log(`  ❌ Error: ${error.message}`);
    }
  }
  
  console.log('\n✅ Test complete! All series checked.');
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.testBrazilLeagues = extendedService.testBrazilLeagues;
  window.testAllBrazilSeries = extendedService.testAllBrazilSeries;
}

export default extendedService;